import type { PlanType } from '../store/funnelV3Store';
import { getDynamicPricingAnalyticsProps, isDynamicPricingEnabled } from './dynamicPricingAb';

export const PRICING_TIER_STORAGE_KEY = 'mt_pricing_tier';

export type PricingTier = 'L' | 'M1' | 'M2' | 'H';

/** L / M / H bucket for reporting — M1 and M2 both roll up to M. */
export type PricingBand = 'L' | 'M' | 'H';

export type PricingTierSignals = {
    commitment?: string | null;
    targetDate?: string | null;
    mainBarrier?: string | null;
    walkingHistory?: string | null;
    motivation?: string | null;
    sleepQuality?: string | null;
    activityLevel?: string | null;
    primaryGoal?: string | null;
    rewardPreference?: string | null;
    currentWeightKg?: number | null;
    goalWeightKg?: number | null;
};

export type TierPricesCents = Record<'1_week_trial' | '4_week' | '12_week', number>;

export type PricingTierResult = {
    tier: PricingTier;
    cpScore: number;
    vpScore: number;
    recommendedPricesCents: TierPricesCents;
    shadowRecommendedDefaultPlan: PlanType;
};

const VALID_TIERS: readonly PricingTier[] = ['L', 'M1', 'M2', 'H'];
const COOKIE_MAX_AGE_SEC = 365 * 24 * 60 * 60;

/** P1 H-tier gate — top-quartile scores only (P0 was vp>=58, cp>=48; assigned 59% of leads to H). */
export const H_TIER_MIN_VP_SCORE = 69;
export const H_TIER_MIN_CP_SCORE = 70;

/**
 * L-band ceiling (exclusive): cp < this → L.
 * P2 widened from 42 → 48 so more price-sensitive mid leads see Join-beating prices.
 */
export const L_TIER_MAX_CP_SCORE = 48;

/** M-band split: lower CP (48–56) → discounted M1; upper M (57–69) → standard M2. */
export const M1_TIER_MAX_CP_SCORE = 56;

/**
 * Classic Walkmore static intro prices (pre week-2 / pre dynamic tiers).
 * Display + checkout base when dynamic pricing is off.
 */
export const STATIC_WALKMORE_PRICES_CENTS: TierPricesCents = {
    '1_week_trial': 499,
    '4_week': 1799,
    '12_week': 2999,
};

/** Original crossed / compare-at prices paired with classic static intros. */
export const STATIC_WALKMORE_STRIKETHROUGH_CENTS: TierPricesCents = {
    '1_week_trial': 999,
    '4_week': 2999,
    '12_week': 5999,
};

/** Exit-intent = 60% off classic strikethrough (40% of original). */
export const STATIC_WALKMORE_EXIT_INTENT_PRICES: Record<PlanType, number> = {
    '1_week_trial': 3.99,
    '4_week': 11.99,
    '12_week': 23.99,
};

/**
 * Walkmore intro prices — classic static ($4.99 / $17.99 / $29.99) on all tiers
 * while dynamic pricing is off. Renewal stays $29.99 / 4 weeks.
 */
export const TIER_RECOMMENDED_PRICES_CENTS: Record<PricingTier, TierPricesCents> = {
    L: { '1_week_trial': 499, '4_week': 1799, '12_week': 2999 },
    M1: { '1_week_trial': 499, '4_week': 1799, '12_week': 2999 },
    M2: { '1_week_trial': 499, '4_week': 1799, '12_week': 2999 },
    H: { '1_week_trial': 499, '4_week': 1799, '12_week': 2999 },
};

/** All walkmore subscriptions renew every 4 weeks at this price after intro/trial. */
export const WALKMORE_RENEWAL_PRICE_CENTS = 2999;

/** Paywall compare-at / strikethrough prices (cents) — classic crossed amounts. */
export const TIER_STRIKETHROUGH_PRICES_CENTS: Record<PricingTier, TierPricesCents> = {
    L: { '1_week_trial': 999, '4_week': 2999, '12_week': 5999 },
    M1: { '1_week_trial': 999, '4_week': 2999, '12_week': 5999 },
    M2: { '1_week_trial': 999, '4_week': 2999, '12_week': 5999 },
    H: { '1_week_trial': 999, '4_week': 2999, '12_week': 5999 },
};

/** Exit-intent prices = 60% off strikethrough (40% of original). */
export const TIER_EXIT_INTENT_PRICES: Record<PricingTier, Record<PlanType, number>> = {
    L: { '1_week_trial': 3.99, '4_week': 11.99, '12_week': 23.99 },
    M1: { '1_week_trial': 3.99, '4_week': 11.99, '12_week': 23.99 },
    M2: { '1_week_trial': 3.99, '4_week': 11.99, '12_week': 23.99 },
    H: { '1_week_trial': 3.99, '4_week': 11.99, '12_week': 23.99 },
};

export function isPricingShadowMode(): boolean {
    return !isDynamicPricingEnabled();
}

export function getPricingBand(tier: PricingTier): PricingBand {
    if (tier === 'L') return 'L';
    if (tier === 'H') return 'H';
    return 'M';
}

/** Tier used for plan fetch + display. Static mode ignores assigned tier for prices. */
export function getPaywallPricingTier(): PricingTier {
    if (!isDynamicPricingEnabled()) {
        return 'M2';
    }
    return getPricingTierResult()?.tier ?? 'M2';
}

export function getPlanBasePricesCents(): TierPricesCents {
    if (!isDynamicPricingEnabled()) {
        return STATIC_WALKMORE_PRICES_CENTS;
    }
    return TIER_RECOMMENDED_PRICES_CENTS[getPaywallPricingTier()];
}

export function getExitIntentPricesForPaywall(): Record<PlanType, number> {
    if (!isDynamicPricingEnabled()) {
        return STATIC_WALKMORE_EXIT_INTENT_PRICES;
    }
    return TIER_EXIT_INTENT_PRICES[getPaywallPricingTier()];
}

/** Compare-at / crossed price shown on paywall + exit-intent (dollars). */
export function getPaywallStrikethroughPrice(planType: PlanType): number {
    if (!isDynamicPricingEnabled()) {
        return STATIC_WALKMORE_STRIKETHROUGH_CENTS[planType] / 100;
    }
    const tier = getPaywallPricingTier();
    return TIER_STRIKETHROUGH_PRICES_CENTS[tier][planType] / 100;
}

export type TierPlanDisplayPrices = {
    displayPrice: number;
    originalPrice: number;
    perDay: number;
    originalPerDay: number;
};

/** Deal UX: intro/display price + higher strikethrough original for every tier/plan type. */
export function getTierPlanDisplayPrices(
    tier: PricingTier,
    planType: PlanType,
    backendPlan?: { firstChargePrice?: number; price: number; intervalCount?: number },
): TierPlanDisplayPrices {
    const intervalCount = backendPlan?.intervalCount
        ?? (planType === '1_week_trial' ? 1 : planType === '4_week' ? 4 : 12);
    const days = intervalCount * 7;

    const useStatic = !isDynamicPricingEnabled();
    const displayPrice = useStatic
        ? STATIC_WALKMORE_PRICES_CENTS[planType] / 100
        : backendPlan
            ? (backendPlan.firstChargePrice || backendPlan.price) / 100
            : TIER_RECOMMENDED_PRICES_CENTS[tier][planType] / 100;

    let originalPrice = (
        useStatic
            ? STATIC_WALKMORE_STRIKETHROUGH_CENTS[planType]
            : TIER_STRIKETHROUGH_PRICES_CENTS[tier][planType]
    ) / 100;
    if (originalPrice <= displayPrice) {
        originalPrice = Math.round(displayPrice * 2 * 100) / 100;
    }

    return {
        displayPrice,
        originalPrice,
        perDay: displayPrice / days,
        originalPerDay: originalPrice / days,
    };
}

export function getSavingsPercent(originalPrice: number, dealPrice: number): number {
    if (originalPrice <= 0 || dealPrice >= originalPrice) return 0;
    return Math.round((1 - dealPrice / originalPrice) * 100);
}

export type StackedDiscountLabels = {
    baseLabel: string | null;
    extraLabel: string | null;
};

/**
 * Paywall badge labels when a Stripe promo stacks on the default first-charge deal
 * (the 40–50% we already show, which is not a coupon).
 */
export function getStackedDiscountLabels(
    baseSavingsPercent: number,
    extraPercent: number,
): StackedDiscountLabels {
    return {
        baseLabel: baseSavingsPercent >= 10 ? `${baseSavingsPercent}% off` : null,
        extraLabel: extraPercent > 0 ? `${extraPercent}% off` : null,
    };
}

type PersistedPricingTier = {
    tier: PricingTier;
    cpScore: number;
    vpScore: number;
};

function isValidTier(value: string): value is PricingTier {
    return (VALID_TIERS as readonly string[]).includes(value);
}

/** Map legacy sticky `M` assignments to M2 (former single mid tier). */
function normalizePricingTier(value: string): PricingTier | null {
    if (value === 'M') return 'M2';
    if (isValidTier(value)) return value;
    return null;
}

function weightToLosePercent(currentWeightKg?: number | null, goalWeightKg?: number | null): number | null {
    if (currentWeightKg == null || goalWeightKg == null || currentWeightKg <= 0 || goalWeightKg >= currentWeightKg) {
        return null;
    }
    return ((currentWeightKg - goalWeightKg) / currentWeightKg) * 100;
}

/** Empirical CP score — higher ≈ more likely to convert (mirrors P0 calibration script). */
export function computeCpScore(signals: PricingTierSignals): number {
    let score = 50;

    if (signals.commitment === 'absolutely') score += 18;
    else if (signals.commitment === 'think_so') score += 6;
    else if (signals.commitment === 'not_sure') score -= 22;

    if (signals.targetDate) score += 12;

    if (signals.mainBarrier === 'uncertainty') score -= 16;
    else if (signals.mainBarrier === 'no_results_fast') score -= 10;
    else if (signals.mainBarrier === 'too_busy') score -= 6;
    else if (signals.mainBarrier === 'lose_motivation') score += 4;

    if (signals.walkingHistory === 'lapsed') score += 8;
    else if (signals.walkingHistory === 'never') score -= 8;

    if (signals.motivation === 'guidance') score += 6;
    else if (signals.motivation === 'gift_cards') score -= 8;

    if (signals.sleepQuality === 'drained') score += 5;

    if (signals.activityLevel === 'light') score += 4;
    else if (signals.activityLevel === 'sedentary') score -= 6;

    const wtLoss = weightToLosePercent(signals.currentWeightKg, signals.goalWeightKg);
    if (wtLoss != null) {
        if (wtLoss >= 10 && wtLoss <= 20) score += 5;
        else if (wtLoss > 30) score -= 8;
        else if (wtLoss < 5) score -= 6;
    }

    return Math.max(0, Math.min(100, score));
}

/** VP score — higher ≈ higher first payment if they convert (mirrors P0 calibration script). */
export function computeVpScore(signals: PricingTierSignals): number {
    let score = 50;

    if (signals.commitment === 'absolutely') score += 15;
    else if (signals.commitment === 'not_sure') score -= 18;

    if (signals.targetDate) score += 10;
    if (signals.walkingHistory === 'lapsed') score += 10;
    if (signals.mainBarrier === 'uncertainty') score -= 12;
    if (signals.motivation === 'gift_cards') score -= 10;
    if (signals.primaryGoal === 'clothes_fit') score -= 5;
    if (signals.primaryGoal === 'health_energy') score += 4;
    if (signals.rewardPreference === 'nike') score += 4;
    if (signals.sleepQuality === 'drained') score += 6;

    return Math.max(0, Math.min(100, score));
}

export function assignPricingTier(cpScore: number, vpScore: number): PricingTier {
    if (cpScore < L_TIER_MAX_CP_SCORE) return 'L';
    if (vpScore >= H_TIER_MIN_VP_SCORE && cpScore >= H_TIER_MIN_CP_SCORE) return 'H';
    if (cpScore <= M1_TIER_MAX_CP_SCORE) return 'M1';
    return 'M2';
}

/** H and M2 default to 12-week (most converters already pick it); L/M1 stay on 4-week. */
export function getShadowDefaultPlanType(tier: PricingTier): PlanType {
    return tier === 'H' || tier === 'M2' ? '12_week' : '4_week';
}

export function buildPricingTierResult(signals: PricingTierSignals): PricingTierResult {
    const cpScore = computeCpScore(signals);
    const vpScore = computeVpScore(signals);
    const tier = assignPricingTier(cpScore, vpScore);

    return {
        tier,
        cpScore,
        vpScore,
        recommendedPricesCents: TIER_RECOMMENDED_PRICES_CENTS[tier],
        shadowRecommendedDefaultPlan: getShadowDefaultPlanType(tier),
    };
}

function readCookie(): string | null {
    if (typeof document === 'undefined') return null;
    const match = document.cookie.match(
        new RegExp(`(?:^|; )${PRICING_TIER_STORAGE_KEY.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}=([^;]*)`)
    );
    return match ? decodeURIComponent(match[1]) : null;
}

function writeCookie(payload: PersistedPricingTier): void {
    if (typeof document === 'undefined') return;
    document.cookie = `${PRICING_TIER_STORAGE_KEY}=${encodeURIComponent(JSON.stringify(payload))};path=/;max-age=${COOKIE_MAX_AGE_SEC};SameSite=Lax`;
}

function readPersistedPricingTier(): PersistedPricingTier | null {
    const raw = typeof localStorage !== 'undefined'
        ? localStorage.getItem(PRICING_TIER_STORAGE_KEY)
        : null;

    const parsePayload = (value: string | null): PersistedPricingTier | null => {
        if (!value) return null;
        try {
            const parsed = JSON.parse(value) as { tier: string; cpScore: number; vpScore: number };
            const tier = normalizePricingTier(parsed.tier);
            if (!tier) return null;
            if (typeof parsed.cpScore !== 'number' || typeof parsed.vpScore !== 'number') return null;
            return { tier, cpScore: parsed.cpScore, vpScore: parsed.vpScore };
        } catch {
            return null;
        }
    };

    const fromStorage = parsePayload(raw);
    if (fromStorage) return fromStorage;

    return parsePayload(readCookie());
}

function persistPricingTier(payload: PersistedPricingTier): void {
    const serialized = JSON.stringify(payload);
    if (typeof localStorage !== 'undefined') {
        localStorage.setItem(PRICING_TIER_STORAGE_KEY, serialized);
    }
    writeCookie(payload);
}

export function getPricingTierResult(): PricingTierResult | null {
    const persisted = readPersistedPricingTier();
    if (!persisted) return null;

    return {
        tier: persisted.tier,
        cpScore: persisted.cpScore,
        vpScore: persisted.vpScore,
        recommendedPricesCents: TIER_RECOMMENDED_PRICES_CENTS[persisted.tier],
        shadowRecommendedDefaultPlan: getShadowDefaultPlanType(persisted.tier),
    };
}

/**
 * Compute and persist pricing tier from quiz signals. Reuses sticky assignment if present.
 * Returns isNewAssignment=true the first time tier is assigned this session/device.
 */
export function ensurePricingTier(signals: PricingTierSignals): { result: PricingTierResult; isNewAssignment: boolean } {
    const existing = readPersistedPricingTier();
    if (existing) {
        return {
            result: {
                tier: existing.tier,
                cpScore: existing.cpScore,
                vpScore: existing.vpScore,
                recommendedPricesCents: TIER_RECOMMENDED_PRICES_CENTS[existing.tier],
                shadowRecommendedDefaultPlan: getShadowDefaultPlanType(existing.tier),
            },
            isNewAssignment: false,
        };
    }

    const result = buildPricingTierResult(signals);
    persistPricingTier({
        tier: result.tier,
        cpScore: result.cpScore,
        vpScore: result.vpScore,
    });

    return { result, isNewAssignment: true };
}

export function buildPricingTierSignalsFromFunnelData(data: PricingTierSignals): PricingTierSignals {
    return {
        commitment: data.commitment ?? null,
        targetDate: data.targetDate ?? null,
        mainBarrier: data.mainBarrier ?? null,
        walkingHistory: data.walkingHistory ?? null,
        motivation: data.motivation ?? null,
        sleepQuality: data.sleepQuality ?? null,
        activityLevel: data.activityLevel ?? null,
        primaryGoal: data.primaryGoal ?? null,
        rewardPreference: data.rewardPreference ?? null,
        currentWeightKg: data.currentWeightKg ?? null,
        goalWeightKg: data.goalWeightKg ?? null,
    };
}

export function getPricingTierAnalyticsProps(): Record<string, string | number> {
    const tierResult = getPricingTierResult();
    const dynamicProps = getDynamicPricingAnalyticsProps();
    if (!tierResult) {
        return dynamicProps;
    }

    const { tier, cpScore, vpScore, recommendedPricesCents, shadowRecommendedDefaultPlan } = tierResult;
    const shadowMode = isPricingShadowMode();

    const props: Record<string, string | number> = {
        ...dynamicProps,
        pricing_tier: tier,
        pricing_band: getPricingBand(tier),
        cp_score: cpScore,
        vp_score: vpScore,
        pricing_shadow_mode: shadowMode ? 1 : 0,
        shadow_price_1w_cents: recommendedPricesCents['1_week_trial'],
        shadow_price_4w_cents: recommendedPricesCents['4_week'],
        shadow_price_12w_cents: recommendedPricesCents['12_week'],
        shadow_recommended_default_plan: shadowRecommendedDefaultPlan,
    };

    if (!shadowMode) {
        props.live_price_1w_cents = recommendedPricesCents['1_week_trial'];
        props.live_price_4w_cents = recommendedPricesCents['4_week'];
        props.live_price_12w_cents = recommendedPricesCents['12_week'];
    }

    return props;
}
